// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

'use client'

import {
  ButtonSize,
  WideNavigationMenuContent,
  WideNavigationMenuItem,
  WideNavigationMenuList,
  WideNavigationMenu as WideNavigationMenuRoot,
  WideNavigationMenuSection,
  WideNavigationMenuSectionItem,
  WideNavigationMenuSectionLink,
  WideNavigationMenuTrigger,
} from '@dafiti/closet-ui'
import type { WithCustomLink } from '@/components/DefaultLink'
import type { MenuItemData } from '@/queries/header'
import { useAppLayoutContext } from '@/providers/AppLayoutProvider'
import { useMessages } from '@/i18n/useMessages'
import * as track from '@/tracking/header'

type WideNavigationMenuProps = {
  data: Array<MenuItemData>
} & Partial<React.ComponentProps<typeof WideNavigationMenuRoot>>

const RecursiveComponent = ({
  item,
  path,
  depth = 0,
  LinkComponent,
}: WithCustomLink<{
  item: MenuItemData
  path: Array<string>
  depth?: number
}>) => {
  const t = useMessages()

  // First-level sections always render as titles, even without children (e.g.
  // "Novedades"), keeping them visually consistent with sibling sections.
  const isTitle = depth === 0 || !!item.items?.length
  const shouldTrack = !!item.url || item.renderItemsAs === 'link'
  const nextPath = [...path, ...(shouldTrack && item.label ? [item.label] : [])]

  const handleClick = () => {
    const complement = nextPath.join(' - ')
    track.menuSubCategoryClick({ complement })
  }

  return (
    <>
      <WideNavigationMenuSectionItem>
        {item.url ? (
          <WideNavigationMenuSectionLink
            as={LinkComponent}
            href={item.url}
            isTitle={isTitle}
            onClick={handleClick}
          >
            {item.label}
          </WideNavigationMenuSectionLink>
        ) : (
          <span className="text-2xs cursor-default font-semibold uppercase leading-7 text-neutral-800">
            {item.label}
          </span>
        )}
      </WideNavigationMenuSectionItem>

      {/* Render a grid of buttons for size selection (used when `renderItemsAs` is "button") */}
      {item.renderItemsAs === 'button' && item.items?.length ? (
        <WideNavigationMenuSectionItem>
          <div className="mb-3 mt-1 grid w-[100px] grid-cols-2 gap-1.5">
            {item.items.map((sizeItem) => {
              const sizePath = [
                ...nextPath,
                ...(sizeItem.label ? [sizeItem.label] : []),
              ].join(' - ')
              return (
                <ButtonSize
                  key={sizeItem.label}
                  as={LinkComponent}
                  href={sizeItem.url}
                  size="sm"
                  className="px-0"
                  onClick={() => {
                    track.menuSubCategoryClick({ complement: sizePath })
                  }}
                >
                  {sizeItem.label}
                </ButtonSize>
              )
            })}
          </div>
        </WideNavigationMenuSectionItem>
      ) : (
        item.items?.map((child, index) => (
          <RecursiveComponent
            key={index}
            item={child}
            path={nextPath}
            depth={depth + 1}
            LinkComponent={LinkComponent}
          />
        ))
      )}

      {/* Render a "See all" link if `seeAll` is provided */}
      {typeof item.seeAll === 'string' && (
        <WideNavigationMenuSectionItem>
          <WideNavigationMenuSectionLink
            as={LinkComponent}
            href={item.seeAll}
            isTitle
            className="mt-3 block normal-case"
            onClick={() =>
              track.menuSubCategoryClick({
                complement: [...nextPath, 'Ver tudo'].join(' - '),
              })
            }
          >
            {t.menu.seeAll}
          </WideNavigationMenuSectionLink>
        </WideNavigationMenuSectionItem>
      )}
    </>
  )
}

export const WideNavigationMenu = ({ data }: WideNavigationMenuProps) => {
  const { LinkComponent, router } = useAppLayoutContext()

  return (
    <WideNavigationMenuRoot
      // Remount on navigation to force-close any open dropdown. The header
      // persists across route changes (App Router layout), so Radix's
      // internal open state would otherwise survive the click.
      key={router?.pathname}
      data-testid="wide-menu"
      className="z-30 hidden lg:block"
    >
      <WideNavigationMenuList>
        {data.map((item, index) => (
          <WideNavigationMenuItem key={index}>
            <WideNavigationMenuTrigger
              as={LinkComponent}
              href={item.url as string}
              onClick={() =>
                track.menuCategoryClick({ complement: item.label as string })
              }
            >
              {item.label}
            </WideNavigationMenuTrigger>

            {item.items && (
              <WideNavigationMenuContent>
                {item.items.map((section, sectionIndex) => (
                  <WideNavigationMenuSection key={sectionIndex}>
                    <RecursiveComponent
                      path={[item.label || '']}
                      item={section}
                      LinkComponent={LinkComponent}
                    />
                  </WideNavigationMenuSection>
                ))}
              </WideNavigationMenuContent>
            )}
          </WideNavigationMenuItem>
        ))}
      </WideNavigationMenuList>
    </WideNavigationMenuRoot>
  )
}
